import { FC, useContext, useMemo, useState } from 'react';
import { Button, FileUploaderDropContainer, TextArea } from '@carbon/react';
import { CheckmarkFilled } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';

import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { Link } from 'components/link';
import { Date } from 'components/date';
import { isTiffFileName } from 'components/allocation-manager/allocation-tracts-table/utils';
import { EMPTY_CELL } from 'utils/empty-cell';
import { formatNumber } from 'utils/number';

import { AfeWizardCtx } from '../context';
import {
  wizardNewUploadsAtom,
  wizardPendingDeletesAtom,
  wizardVisibleFilesAtom,
} from '../atoms';
import type { WizardFileRow } from '../types';
import styles from './supporting-information-step.module.scss';

// `DataTable<T>` constrains `T extends Record<string, unknown>`. Intersect
// the discriminated union with the index signature; TS still narrows by
// `source` inside cells so arm-specific fields stay safely accessible.
type WizardFileTableRow = WizardFileRow & Record<string, unknown>;

/** All-features-off DataTable config — mirrors detail-page supporting-files-panel and summary-step. */
const READONLY_TABLE_CONFIG = {
  sorting: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  columnVisibility: false,
  globalSearch: false,
  columnFilters: false,
  hideFooter: true,
} as const;

/**
 * Step 5 — Supporting Information. Drop zone for multi-file uploads, a
 * combined table of new uploads + existing AFE files (edit mode), per-row
 * Delete with confirmation, and the existing Notes textarea. Reads from
 * the shared `wizardVisibleFilesAtom` so the Summary step renders the
 * identical list. Step has no validation bridge — both fields are optional.
 * @return {JSX.Element} The supporting information step content.
 */
const SupportingInformationStep: FC = () => {
  const { form } = useContext(AfeWizardCtx)!;
  const visibleFiles = useAtomValue(wizardVisibleFilesAtom);
  const setUploads = useSetAtom(wizardNewUploadsAtom);
  const setPendingDeletes = useSetAtom(wizardPendingDeletesAtom);
  const [fileToDelete, setFileToDelete] = useState<WizardFileRow | null>(null);

  const handleConfirmDelete = () => {
    if (!fileToDelete) return;
    if (fileToDelete.source === 'new') {
      setUploads({ type: 'hide', localId: fileToDelete.localId });
    } else {
      const { existingId } = fileToDelete;
      // Jotai compares with Object.is, so writers must produce a NEW Set
      // instance — in-place `prev.add(id)` would be invisible.
      setPendingDeletes((prev) => new Set(prev).add(existingId));
    }
    setFileToDelete(null);
  };

  const columns = useMemo<DataTableColumnDef<WizardFileTableRow>[]>(
    () => [
      {
        id: 'fileName',
        accessorKey: 'name',
        header: 'File Name',
        size: 460,
        meta: { type: 'text' },
        cell: ({ row }) => {
          const r = row.original;
          // Existing files are always uploaded; new files only once status flips
          // to 'complete' (blob signed_id materialized).
          const isUploaded = r.source === 'existing' || r.status === 'complete';
          const nameNode = r.href ? (
            <Link
              truncate
              to={r.href}
              target="_blank"
              rel="noreferrer"
              onClick={(e: React.MouseEvent) => {
                // TIFF/TIF inline rendering breaks in some browsers — force
                // window.open so the user gets a download/handler prompt
                // instead of a broken tab. Same workaround the detail panel
                // and well-page-content panel both carry.
                if (isTiffFileName(r.name)) {
                  e.preventDefault();
                  window.open(r.href!, '_blank');
                }
              }}
            >
              {r.name}
            </Link>
          ) : (
            r.name
          );
          return (
            <span className={styles.fileNameCell}>
              {nameNode}
              {isUploaded && (
                <CheckmarkFilled
                  size={16}
                  className={styles.uploadedIcon}
                  aria-label="Uploaded"
                />
              )}
            </span>
          );
        },
      },
      {
        id: 'size',
        header: 'Size',
        size: 100,
        meta: { type: 'text' },
        cell: ({ row }) => row.original.size ?? EMPTY_CELL,
      },
      {
        id: 'uploaded',
        accessorKey: 'date',
        header: 'Uploaded',
        size: 130,
        meta: { type: 'text' },
        cell: ({ row }) => {
          const date = row.original.date;
          return date ? <Date date={date} format="M/DD/YYYY" /> : EMPTY_CELL;
        },
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        cell: ({ row }) => (
          <Button
            kind="ghost"
            size="sm"
            onClick={() => setFileToDelete(row.original)}
          >
            Delete
          </Button>
        ),
      },
    ],
    []
  );

  const count = visibleFiles.length;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Supporting Information</h2>
      <p className={styles.subtitle}>
        Fill in lines items of Costs and click continue.
      </p>

      <div className={styles.content}>
        <div className={styles.uploadSection}>
          <span className={styles.uploadLabel}>
            Upload Documents (Optional)
          </span>
          <FileUploaderDropContainer
            labelText="Drag and drop files here or click to upload"
            multiple
            onAddFiles={(_event, { addedFiles }) => {
              if (addedFiles.length > 0) {
                setUploads({ type: 'addNew', files: addedFiles });
              }
            }}
          />
        </div>

        {count > 0 && (
          <>
            <div className={styles.tableContainer}>
              <DataTable<WizardFileTableRow>
                data={visibleFiles as WizardFileTableRow[]}
                columns={columns}
                getRowId={(row) => row.key}
                config={READONLY_TABLE_CONFIG}
              />
            </div>
            <p className={styles.counter}>
              {formatNumber({
                value: count,
                minPrecision: 0,
                maxPrecision: 0,
              })}{' '}
              {count === 1 ? 'document' : 'documents'} uploaded
            </p>
          </>
        )}

        <form.Field name="notes">
          {(field) => (
            <TextArea
              id="afe-notes"
              labelText="Notes (Optional)"
              placeholder="Notes"
              enableCounter
              maxCount={100}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value.slice(0, 100))}
            />
          )}
        </form.Field>
      </div>

      <DestroyConfirmation
        open={fileToDelete !== null}
        onRequestClose={() => setFileToDelete(null)}
        onRequestSubmit={handleConfirmDelete}
        modalHeading="Delete Document"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
      >
        Are you sure you want to delete <strong>{fileToDelete?.name}</strong>?
        This action cannot be undone.
      </DestroyConfirmation>
    </div>
  );
};

export { SupportingInformationStep };
